export interface PixelDimensions {
  width: number;
  height: number;
}

export interface PrintQualityResult {
  formatKey: string;
  actual: PixelDimensions;
  targetAt300Dpi: PixelDimensions;
  effectiveDpi: number;
  minimumDpi: number;
  passes: boolean;
}

// Single source of truth for print dimensions at 300 DPI with 4 mm bleed.
// The storefront re-exports this table from src/constants/imageFormats.ts.
export const PRINT_DIMENSIONS_PX: Readonly<Record<string, PixelDimensions>> = {
  portrait_15x20: { width: 1866, height: 2457 },
  portrait_30x40: { width: 3637, height: 4819 },
  // Retired 40×50 remains measurable for brief storefront-generation compatibility.
  portrait_40x50: { width: 4819, height: 6000 },
  portrait_45x60: { width: 5409, height: 7181 },
  // Retired formats remain measurable for historical support/re-order flows.
  portrait_60x80: { width: 7181, height: 9543 },
  landscape_20x15: { width: 2457, height: 1866 },
  landscape_40x30: { width: 4819, height: 3637 },
  landscape_50x40: { width: 6000, height: 4819 },
  landscape_60x45: { width: 7181, height: 5409 },
  landscape_80x60: { width: 9543, height: 7181 },
  square_30x30: { width: 3637, height: 3637 },
  square_40x40: { width: 4819, height: 4819 },
  square_50x50: { width: 6047, height: 6047 },
};

const SIZE_DIMENSIONS: Readonly<Record<string, string>> = {
  portrait_small: "15x20",
  portrait_medium: "30x40",
  portrait_large: "45x60",
  landscape_small: "20x15",
  landscape_medium: "40x30",
  landscape_large: "60x45",
  square_small: "30x30",
  square_medium: "40x40",
  square_large: "50x50",
};

export function getPrintFormatKey(orientation: string, size: string): string | null {
  const dimensions = SIZE_DIMENSIONS[`${orientation}_${size}`];
  return dimensions ? `${orientation}_${dimensions}` : null;
}

/**
 * Resolves the persisted physical format of an order without collapsing a
 * retired dimension (for example 60×80) into today's meaning of `large`.
 */
export function getPrintFormatKeyForFormatSize(formatSize: string): string | null {
  if (PRINT_DIMENSIONS_PX[formatSize]) return formatSize;

  const semanticMatch = formatSize.match(/^(portrait|landscape|square)_(small|medium|large)$/);
  if (semanticMatch) return getPrintFormatKey(semanticMatch[1], semanticMatch[2]);

  const dimensionsMatch = formatSize.match(/^(?:(portrait|landscape|square)_)?(\d+)x(\d+)$/);
  if (!dimensionsMatch) return null;

  const width = Number.parseInt(dimensionsMatch[2]);
  const height = Number.parseInt(dimensionsMatch[3]);
  const orientation = dimensionsMatch[1]
    || (width > height ? "landscape" : width < height ? "portrait" : "square");
  const key = `${orientation}_${dimensionsMatch[2]}x${dimensionsMatch[3]}`;
  return PRINT_DIMENSIONS_PX[key] ? key : null;
}

export function parseMinimumDpi(value: string | undefined, fallback = 150): number {
  if (value === undefined || value.trim() === "") return fallback;

  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0 || parsed > 300) {
    throw new Error("GELATO_MIN_DPI must be a number greater than 0 and no greater than 300");
  }
  return parsed;
}

export function evaluatePrintQuality(
  actual: PixelDimensions,
  formatKey: string,
  minimumDpi: number,
): PrintQualityResult {
  const targetAt300Dpi = PRINT_DIMENSIONS_PX[formatKey];
  if (!targetAt300Dpi) throw new Error(`No print-resolution target for format ${formatKey}`);
  if (actual.width <= 0 || actual.height <= 0) throw new Error("Invalid image dimensions");

  const effectiveDpi = 300 * Math.min(
    actual.width / targetAt300Dpi.width,
    actual.height / targetAt300Dpi.height,
  );

  return {
    formatKey,
    actual,
    targetAt300Dpi,
    effectiveDpi,
    minimumDpi,
    passes: effectiveDpi >= minimumDpi,
  };
}

export function parseImageDimensions(bytes: Uint8Array): PixelDimensions {
  const pngSignature = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];
  if (bytes.length >= 24 && pngSignature.every((value, index) => bytes[index] === value)) {
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    const width = view.getUint32(16);
    const height = view.getUint32(20);
    if (width > 0 && height > 0) return { width, height };
    throw new Error("PNG header contains invalid dimensions");
  }

  if (bytes.length >= 4 && bytes[0] === 0xff && bytes[1] === 0xd8) {
    let offset = 2;
    while (offset + 3 < bytes.length) {
      if (bytes[offset] !== 0xff) {
        offset++;
        continue;
      }

      while (offset < bytes.length && bytes[offset] === 0xff) offset++;
      if (offset >= bytes.length) break;
      const marker = bytes[offset++];
      if (marker === 0xd8 || marker === 0xd9 || marker === 0x01) continue;
      if (marker >= 0xd0 && marker <= 0xd7) continue;
      if (offset + 1 >= bytes.length) break;

      const segmentLength = (bytes[offset] << 8) | bytes[offset + 1];
      if (segmentLength < 2 || offset + segmentLength > bytes.length) break;

      const isStartOfFrame = [
        0xc0, 0xc1, 0xc2, 0xc3, 0xc5, 0xc6, 0xc7,
        0xc9, 0xca, 0xcb, 0xcd, 0xce, 0xcf,
      ].includes(marker);
      if (isStartOfFrame) {
        if (segmentLength < 7) throw new Error("JPEG frame header is invalid");
        const height = (bytes[offset + 3] << 8) | bytes[offset + 4];
        const width = (bytes[offset + 5] << 8) | bytes[offset + 6];
        if (width > 0 && height > 0) return { width, height };
        throw new Error("JPEG header contains invalid dimensions");
      }

      offset += segmentLength;
    }
    throw new Error("JPEG dimensions were not found in the inspected header");
  }

  throw new Error("Unsupported or unreadable image header; expected JPEG or PNG");
}

export async function readImageHeader(
  imageUrl: string,
  fetcher: typeof fetch = fetch,
  maximumBytes = 64 * 1024,
): Promise<Uint8Array> {
  const url = new URL(imageUrl);
  if (url.protocol !== "https:" || url.username || url.password) {
    throw new Error("Print asset URL must be an HTTPS URL without credentials");
  }

  const response = await fetcher(imageUrl, {
    headers: { Range: `bytes=0-${maximumBytes - 1}` },
    // Do not let a user-controlled asset URL redirect the Edge Function to an
    // internal service. Generated assets are stored at stable public URLs and
    // should not need redirects.
    redirect: "error",
  });
  if (!response.ok) throw new Error(`Unable to inspect print asset: HTTP ${response.status}`);
  if (!response.body) throw new Error("Unable to inspect print asset: empty response body");

  const reader = response.body.getReader();
  const chunks: Uint8Array[] = [];
  let total = 0;
  try {
    while (total < maximumBytes) {
      const { value, done } = await reader.read();
      if (done) break;
      const remaining = maximumBytes - total;
      const chunk = value.length > remaining ? value.subarray(0, remaining) : value;
      chunks.push(chunk);
      total += chunk.length;
      if (chunk.length < value.length) break;
    }
  } finally {
    await reader.cancel().catch(() => undefined);
  }

  const bytes = new Uint8Array(total);
  let offset = 0;
  for (const chunk of chunks) {
    bytes.set(chunk, offset);
    offset += chunk.length;
  }
  return bytes;
}
